import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";
import { buildSpecialtySummaryQuery } from "./specialtySummaryQuery";

const API = environment.apiUrl;
const base = `${API}/karoline`;

const get = async (endpoint) => {
  const res = await customFetch(`${base}${endpoint}`);
  return res.json();
};

const post = async (endpoint, body) => {
  const res = await customFetch(`${base}${endpoint}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
};

const put = async (endpoint, body) => {
  const res = await customFetch(`${base}${endpoint}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json();
};

const del = async (endpoint) => {
  const res = await customFetch(`${base}${endpoint}`, { method: "DELETE" });
  return res.json();
};

// Filters
export const fetchVISNs = () => get("/filters/visns");
export const fetchStations = (visn) =>
  get(`/filters/stations${visn ? `?visn=${visn}` : ""}`);
export const fetchSpecialties = () => get("/filters/specialties");
export const fetchSpecialtyStationMap = () => get("/filters/specialty-station-map");
export const fetchWeeks = () => get("/filters/weeks");

// Reports
export const fetchLicensedGrowth = () => get("/reports/licensed-growth");
export const fetchLaunchedGrowth = () => get("/reports/launched-growth");
export const fetchActiveGrowth = () => get("/reports/active-growth");
export const fetchDecommissionedGrowth = () => get("/reports/decommissioned-growth");
export const fetchGrowth30d = () => get("/reports/growth-30d");
export const fetchLambdaExcluded = () => get("/reports/lambda-excluded");
export const fetchActivitySummary = () => get("/reports/activity-summary");
export const fetchSpecialtySummary = ({ visn, new_visn } = {}) => {
  const qs = buildSpecialtySummaryQuery({ visn, new_visn });
  return get(`/reports/specialty-summary${qs ? `?${qs}` : ""}`);
};
export const fetchActivityBreakdown = (groupBy) =>
  get(`/reports/activity-breakdown?group_by=${groupBy}`);
export const fetchStationUtilization = (stationId, { exact } = {}) =>
  get(`/reports/station-utilization?station_id=${encodeURIComponent(stationId)}${exact ? "&exact=true" : ""}`);
export const fetchSpecialtyUtilization = (specialty) =>
  get(`/reports/specialty-utilization?specialty=${encodeURIComponent(specialty)}`);
export const fetchVisnUtilization = ({ visn, new_visn } = {}) => {
  const params = new URLSearchParams();
  if (visn) params.set("visn", visn);
  if (new_visn) params.set("new_visn", new_visn);
  return get(`/reports/visn-utilization?${params}`);
};
export const fetchRecordingLeaderboard = ({ days = 30, cumulative = false, specialty, visn, station_id } = {}) => {
  const params = new URLSearchParams({ days });
  if (cumulative) params.set("cumulative", "true");
  if (specialty) params.set("specialty", specialty);
  if (visn) params.set("visn", visn);
  if (station_id) params.set("station_id", station_id);
  return get(`/reports/recording-leaderboard?${params}`);
};
export const fetchActivityDetail = (type, { groupBy, groupKey } = {}) => {
  const params = new URLSearchParams({ type });
  if (groupBy && groupKey != null) {
    params.set("group_by", groupBy);
    params.set("group_key", groupKey);
  }
  return get(`/reports/activity-detail?${params}`);
};

export const fetchMonthlyReport = ({ report_year, report_month, visn, station_id, specialty, page, limit, exclude_decommissioned }) => {
  const params = new URLSearchParams({ report_year, report_month });
  if (visn) params.set("visn", visn);
  if (station_id) params.set("station_id", station_id);
  if (specialty) params.set("specialty", specialty);
  if (page) params.set("page", page);
  if (limit) params.set("limit", limit);
  if (exclude_decommissioned) params.set("exclude_decommissioned", "true");
  return get(`/reports/monthly?${params}`);
};

// Batches month-count lookups into one /reports/monthly/totals request instead of one per month; closed months are cached per tab (sessionStorage), current month always refetched.
const monthlyTotalCache = new Map();
const MONTHLY_TOTAL_STORAGE_PREFIX = "karoline_monthly_total_";
// Filter set is part of the cache key so a filtered call can't read/overwrite an unfiltered call's cached total for the same month.
const normalizeFilterKey = ({ visn, station_id, specialty, exclude_decommissioned } = {}) =>
  `${visn || ""}|${station_id || ""}|${specialty || ""}|${exclude_decommissioned ? "1" : "0"}`;
const monthlyTotalCacheKey = (year, month, filterKey) => `${year}${String(month).padStart(2, "0")}::${filterKey}`;
const isCurrentMonth = (year, month) => {
  const now = new Date();
  return Number(year) === now.getFullYear() && Number(month) === now.getMonth() + 1;
};

const fetchMonthlyReportTotals = ({ months, visn, station_id, specialty, exclude_decommissioned }) => {
  const params = new URLSearchParams({ months: months.join(",") });
  if (visn) params.set("visn", visn);
  if (station_id) params.set("station_id", station_id);
  if (specialty) params.set("specialty", specialty);
  if (exclude_decommissioned) params.set("exclude_decommissioned", "true");
  return get(`/reports/monthly/totals?${params}`);
};

// periods: array of "YYYYMM" strings. Returns { [period]: total }.
export const fetchMonthlyReportTotalsBatch = async (periods, { visn, station_id, specialty, exclude_decommissioned = true } = {}) => {
  const result = {};
  const toFetch = [];
  const filterKey = normalizeFilterKey({ visn, station_id, specialty, exclude_decommissioned });

  for (const period of periods) {
    const year = period.slice(0, 4);
    const month = parseInt(period.slice(4, 6), 10);
    const key = monthlyTotalCacheKey(year, month, filterKey);

    if (!isCurrentMonth(year, month)) {
      if (monthlyTotalCache.has(key)) { result[period] = monthlyTotalCache.get(key); continue; }
      try {
        const stored = sessionStorage.getItem(MONTHLY_TOTAL_STORAGE_PREFIX + key);
        if (stored != null) {
          const total = JSON.parse(stored);
          monthlyTotalCache.set(key, total);
          result[period] = total;
          continue;
        }
      } catch {
        // sessionStorage unavailable/blocked — fall through to a live fetch
      }
    }
    toFetch.push(period);
  }

  if (toFetch.length > 0) {
    const res = await fetchMonthlyReportTotals({ months: toFetch, visn, station_id, specialty, exclude_decommissioned });
    for (const row of res.data || []) {
      const period = String(row.month);
      const total = typeof row.total === "number" ? row.total : parseInt(row.total, 10);
      result[period] = total;

      const year = period.slice(0, 4);
      const month = parseInt(period.slice(4, 6), 10);
      if (!isCurrentMonth(year, month)) {
        const key = monthlyTotalCacheKey(year, month, filterKey);
        monthlyTotalCache.set(key, total);
        try {
          sessionStorage.setItem(MONTHLY_TOTAL_STORAGE_PREFIX + key, JSON.stringify(total));
        } catch {
          // ignore quota/availability errors
        }
      }
    }
  }

  return result;
};

// Convenience single-period wrapper around fetchMonthlyReportTotalsBatch.
export const fetchMonthlyReportTotal = async ({ report_year, report_month, exclude_decommissioned = true, ...rest }) => {
  const period = `${report_year}${String(report_month).padStart(2, "0")}`;
  const map = await fetchMonthlyReportTotalsBatch([period], { exclude_decommissioned, ...rest });
  return map[period] ?? null;
};

export const fetchStationWeeklyHistory = ({ station_id }) => {
  const params = new URLSearchParams({ station_id });
  return get(`/reports/weekly/history?${params}`);
};

export const fetchRecentWeekly = ({ station_id, limit = 4 }) => {
  const params = new URLSearchParams({ station_id, limit });
  return get(`/reports/weekly/recent?${params}`);
};

export const fetchWeeklyReport = ({ report_week, visn, station_id }) => {
  const params = new URLSearchParams({ report_week });
  if (visn) params.set("visn", visn);
  if (station_id) params.set("station_id", station_id);
  return get(`/reports/weekly?${params}`);
};

export const downloadCSV = async (endpoint, filename) => {
  const res = await customFetch(`${base}${endpoint}`);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const exportMonthlyCSV = ({ report_year, report_month, visn, station_id, specialty, exclude_decommissioned }) => {
  const params = new URLSearchParams({ report_year, report_month });
  if (visn) params.set("visn", visn);
  if (station_id) params.set("station_id", station_id);
  if (specialty) params.set("specialty", specialty);
  if (exclude_decommissioned) params.set("exclude_decommissioned", "true");
  return downloadCSV(`/reports/monthly/export?${params}`, `monthly_report_${report_year}_${report_month}.csv`);
};

export const exportStationWeeklyHistoryCSV = ({ station_id }) => {
  const params = new URLSearchParams();
  if (station_id) params.set("station_id", station_id);
  return downloadCSV(`/reports/weekly/history/export?${params}`, `weekly_history_station_${station_id}.csv`);
};

export const exportWeeklyCSV = ({ report_week, visn, station_id }) => {
  const params = new URLSearchParams({ report_week });
  if (visn) params.set("visn", visn);
  if (station_id) params.set("station_id", station_id);
  return downloadCSV(`/reports/weekly/export?${params}`, `weekly_report_${report_week}.csv`);
};

// Providers
export const fetchProviders = ({ station_id, specialty, search, page, limit, allPeriods } = {}) => {
  const params = new URLSearchParams();
  if (station_id) params.set("station_id", station_id);
  if (specialty) params.set("specialty", specialty);
  if (search) params.set("search", search);
  if (page) params.set("page", page);
  if (limit) params.set("limit", limit);
  if (allPeriods) params.set("all_periods", "true");
  return get(`/providers?${params}`);
};

export const fetchDecommissionedProviders = ({ station_id, visn, search, page, limit } = {}) => {
  const params = new URLSearchParams();
  if (station_id) params.set("station_id", station_id);
  if (visn) params.set("visn", visn);
  if (search) params.set("search", search);
  if (page) params.set("page", page);
  if (limit) params.set("limit", limit);
  return get(`/providers/decommissioned?${params}`);
};

export const batchLookupProviders = ({ raws, emails, namePatterns }) =>
  post("/providers/batch-lookup", { raws, emails, namePatterns });
export const bulkLookupProviders = ({ raws, emails, namePatterns }) =>
  post("/providers/bulk-lookup", { raws, emails, namePatterns });
export const fetchProvider = (secid) => get(`/providers/${secid}`);
export const updateProvider = (secid, data) => put(`/providers/${secid}`, data);
export const deleteProvider = (secid) => del(`/providers/${secid}`);
export const uploadProviders = (report_year, report_month, providers) =>
  post("/providers/upload", { report_year, report_month, providers });

// Help Desk (Pylon)
export const fetchPylonIssues = ({ state, cursor, limit, search } = {}) => {
  const params = new URLSearchParams();
  if (state) params.set("state", state);
  if (cursor) params.set("cursor", cursor);
  if (limit) params.set("limit", limit);
  if (search) params.set("search", search);
  return get(`/helpdesk/issues?${params}`);
};

export const fetchPylonIssue = (id) => get(`/helpdesk/issues/${id}`);

export const fetchStarRatings = ({ cursor, limit } = {}) => {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit) params.set("limit", limit);
  return get(`/helpdesk/ratings?${params}`);
};

// License goal
export const fetchLicenseGoal = ({ year, quarter } = {}) => {
  const params = new URLSearchParams();
  if (year) params.set("year", year);
  if (quarter) params.set("quarter", quarter);
  const qs = params.toString();
  return get(`/goals/license${qs ? `?${qs}` : ""}`);
};
export const fetchLicenseGoalProgress = ({ year, quarter }) =>
  get(`/goals/license/progress?year=${year}&quarter=${quarter}`);
export const fetchProvisionedCountAsOf = (date) => get(`/goals/license/count?date=${date}`);
export const saveLicenseGoal = (goal) => put("/goals/license", goal);

// Stations
export const fetchStationList = () => get("/stations");
export const upsertStations = (stations) => post("/stations", { stations });
export const deleteStation = (station_id) => del(`/stations/${station_id}`);

// Outreach — standalone endpoint at /api/outreach (not nested under /api/karoline) so it can be split out later; capability gate lives in server/config/capabilities.js.
const outreachBase = `${API}/outreach`;

// customFetch only throws on network/auth failures, so preserve status + body here for callers to distinguish 404/409/etc.
const outreachRequest = async (path, options) => {
  const res = await customFetch(`${outreachBase}${path}`, options);
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON body, leave null */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
};

// Server-side paginated (limit/offset); search matches provider name/email, station, specialty, and last attempt's staffer. Response is { rows, total, limit, offset }.
export const fetchOutreachCandidates = ({ limit, offset, search, priority, attempts, sort, dir, reason } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", limit);
  if (offset != null) params.set("offset", offset);
  if (search) params.set("search", search);
  if (priority && priority.length > 0) params.set("priority", priority.join(","));
  if (attempts && attempts.length > 0) params.set("attempts", attempts.join(","));
  if (reason) params.set("reason", reason);
  if (sort) params.set("sort", sort);
  if (dir) params.set("dir", dir);
  const qs = params.toString();
  return outreachRequest(`${qs ? `?${qs}` : ""}`);
};

// Same filters as the list, but unpaginated and with each candidate's attempts nested under `attempts` for the XLSX export.
export const fetchOutreachExport = ({ search, priority, attempts, sort, dir, reason } = {}) => {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (priority && priority.length > 0) params.set("priority", priority.join(","));
  if (attempts && attempts.length > 0) params.set("attempts", attempts.join(","));
  if (reason) params.set("reason", reason);
  if (sort) params.set("sort", sort);
  if (dir) params.set("dir", dir);
  const qs = params.toString();
  return outreachRequest(`/export${qs ? `?${qs}` : ""}`);
};

export const fetchOutreachDetail = (doctorUserId) => outreachRequest(`/${doctorUserId}`);

// Same patients.user_feedback ratings the User Feedback dashboard shows,
// scoped to this outreach candidate.
export const fetchOutreachUserFeedback = (doctorUserId, { limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", limit);
  if (offset != null) params.set("offset", offset);
  const qs = params.toString();
  return outreachRequest(`/${doctorUserId}/user-feedback${qs ? `?${qs}` : ""}`);
};

// attempt_by comes from localStorage's "username" (set at login); body is shaped explicitly so retired fields can't leak into the request.
export const logOutreachAttempt = (doctorUserId, { attempt_type, provider_feedback, attempt_status, attempt_note }) =>
  outreachRequest(`/${doctorUserId}/attempts`, {
    method: "POST",
    body: JSON.stringify({
      attempt_type,
      provider_feedback,
      attempt_status,
      attempt_note,
      attempt_by: localStorage.getItem("username") || undefined,
    }),
  });

// Only status/feedback are editable after the fact; everything else about an attempt is immutable history.
export const updateOutreachAttempt = (doctorUserId, attemptNumber, { attempt_status, provider_feedback }) =>
  outreachRequest(`/${doctorUserId}/attempts/${attemptNumber}`, {
    method: "PUT",
    body: JSON.stringify({ attempt_status, provider_feedback }),
  });

// Manual removal only — the only path that sets removed_at. out_of_attempts is written by logOutreachAttempt on the 3rd attempt without removing the candidate; launched is reserved for the cron.
export const removeOutreachCandidate = (doctorUserId, reason) =>
  outreachRequest(`/${doctorUserId}/remove`, {
    method: "POST",
    body: JSON.stringify({ reason_for_archive: reason }),
  });
